"use client";

import type { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardTitle } from "@velpha/ui/components/card";

// ── Color tokens ────────────────────────────────────────────────────

export const COLOR = {
  sky: "#0284c7",
  rose: "#e11d48",
  emerald: "#16a34a",
  amber: "#d97706",
  purple: "#9333ea",
} as const;

// ── Types ───────────────────────────────────────────────────────────

export type RiskLevel = "low" | "medium" | "high";

export type RiskExample = {
  level: RiskLevel;
  description: string;
  series: number[];
};

// ── SVG utilities ───────────────────────────────────────────────────

export const SVG = { w: 200, h: 90, px: 15, py: 10 } as const;

export function seriesToPoints(
  series: number[],
  bounds: { min: number; max: number; w: number; h: number; px: number; py: number }
) {
  const { min, max, w, h, px, py } = bounds;
  return series.map((v, i) => ({
    x: px + (i / (series.length - 1)) * (w - px * 2),
    y: py + ((max - v) / (max - min)) * (h - py * 2),
  }));
}

export function pointsToLinePath(pts: { x: number; y: number }[]) {
  return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

export function pointsToAreaPath(pts: { x: number; y: number }[], bottom: number) {
  const line = pointsToLinePath(pts);
  return `${line} L ${pts[pts.length - 1].x} ${bottom} L ${pts[0].x} ${bottom} Z`;
}

export function GridLine({ y, opacity = 0.12 }: { y: number; opacity?: number }) {
  return (
    <line x1={SVG.px} y1={y} x2={SVG.w - SVG.px} y2={y} stroke="currentColor" strokeDasharray="3 6" opacity={opacity} />
  );
}

export function Dot({ x, y, color, r = 3.5 }: { x: number; y: number; color: string; r?: number }) {
  return <circle cx={x} cy={y} r={r} fill="white" stroke={color} strokeWidth="1.5" />;
}

export function MeasureArrow({
  x1,
  y1,
  x2,
  y2,
  color,
  label,
  labelOffset = 0,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  label?: string;
  labelOffset?: number;
}) {
  const isVertical = x1 === x2;
  const cap = 3;
  return (
    <g opacity="0.65">
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.5" />
      {isVertical ? (
        <>
          <line x1={x1 - cap} y1={y1} x2={x1 + cap} y2={y1} stroke={color} strokeWidth="1.5" />
          <line x1={x2 - cap} y1={y2} x2={x2 + cap} y2={y2} stroke={color} strokeWidth="1.5" />
        </>
      ) : (
        <>
          <path
            d={`M ${x1 + cap} ${y1 - cap} L ${x1} ${y1} L ${x1 + cap} ${y1 + cap}`}
            fill="none"
            stroke={color}
            strokeWidth="1.2"
          />
          <path
            d={`M ${x2 - cap} ${y2 - cap} L ${x2} ${y2} L ${x2 - cap} ${y2 + cap}`}
            fill="none"
            stroke={color}
            strokeWidth="1.2"
          />
        </>
      )}
      {label ? (
        <text
          x={isVertical ? x1 + 6 + labelOffset : (x1 + x2) / 2}
          y={isVertical ? (y1 + y2) / 2 : y1 + 9 + labelOffset}
          textAnchor={isVertical ? "start" : "middle"}
          dominantBaseline={isVertical ? "central" : "auto"}
          fontSize="7"
          fill={color}
          fontWeight="500"
          opacity="0.9"
        >
          {label}
        </text>
      ) : null}
    </g>
  );
}

// ── Shared layout ───────────────────────────────────────────────────

export function IllustrationCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card className="border p-3 gap-2!">
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
